import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../axiosconfig";
import { normalizeAge, normalizeGender } from "../constants/profileConstants";

const CACHE_KEY = "mindsherpa_profile_data";
const DRAFT_KEY = "mindsherpa_profile_draft";

const getCachedProfile = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const getCachedDraft = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const emptyData = {
  displayName: "",
  firstName: "",
  lastName: "",
  age: "",
  gender: "",
  country: "",
  language: "",
  phone: "",
  profilePictureUrl: "",
  email: "",
};

const cleanVal = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (s.toLowerCase() === "null" || s.toLowerCase() === "none") return "";
  return s;
};

export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post("/profile", { request: "getUserData" });
      if (res?.data?.result === 1 && res.data.data) {
        const u = res.data.data;
        const mapped = {
          displayName: u.name || "",
          firstName: u.firstname || "",
          lastName: u.lastname || "",
          age: normalizeAge(u.age) || "",
          gender: normalizeGender(u.gender) || "",
          country: cleanVal(u.location),
          language: cleanVal(u.language),
          phone: cleanVal(u.phonenumber),
          profilePictureUrl: u.profilePictureUrl || "",
          email: u.email || "",
          profilepicturepath: u.profilepicturepath || "",
        };
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
        } catch (e) {}
        return mapped;
      }
      return rejectWithValue(res?.data?.message || "Failed to load profile");
    } catch (err) {
      return rejectWithValue(err?.message || "Network error");
    }
  }
);

const initialState = {
  data: getCachedProfile() || emptyData,
  draft: getCachedDraft() || {},
  status: "idle",
  error: null,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setProfileData(state, action) {
      state.data = { ...state.data, ...action.payload };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(state.data));
      } catch (e) {}
    },
    setProfileDraft(state, action) {
      state.draft = { ...state.draft, ...action.payload };
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(state.draft));
      } catch (e) {}
    },
    clearProfileDraft(state) {
      state.draft = {};
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
    },
    clearProfile(state) {
      state.data = emptyData;
      state.draft = {};
      state.status = "idle";
      state.error = null;
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = { ...state.data, ...action.payload };
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setProfileData,
  setProfileDraft,
  clearProfileDraft,
  clearProfile,
} = profileSlice.actions;

export const selectProfileData = (state) => state.profile?.data || emptyData;
export const selectProfileDraft = (state) => state.profile?.draft || {};
export const selectActiveProfile = (state) => ({
  ...(state.profile?.data || emptyData),
  ...(state.profile?.draft || {}),
});
export const selectProfileDisplayName = (state) =>
  state.profile?.draft?.displayName ||
  state.profile?.data?.displayName ||
  state.profile?.data?.firstName ||
  state.auth?.user?.name ||
  "";

export default profileSlice.reducer;
